import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '../common/Alert.js';
import shipmentClientService from '../../services/ShipmentClientService.js';
import { FileText, Download, DollarSign, Filter } from 'lucide-react';
import { PageLayout, Button, DataTable, FilterPanel, StatusBadge, DetailCard, FormField } from '../common/index.js';

const VendorBatchShipmentBill = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [client, setClient] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [summary, setSummary] = useState({
    total_shipments: 0,
    total_amount: 0,
    paid_amount: 0,
    balance_amount: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  
  // Payment states
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  
  useEffect(() => {
    if (clientId) {
      fetchClientShipments();
    }
  }, [clientId]);

  const fetchClientShipments = async (applyFilters = false) => {
    try {
      setLoading(true);
      const params = {};
      
      if (applyFilters && fromDate) params.from_date = fromDate;
      if (applyFilters && toDate) params.to_date = toDate;
      
      const response = await shipmentClientService.getClientShipments(clientId, params);
      
      if (response.success) {
        setClient(response.client);
        setShipments(response.shipments);
        setSummary(response.summary);
      }
    } catch (err) {
      Alert.error('Failed to load client shipments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    fetchClientShipments(true);
  };

  const handleClearFilters = () => {
    setFromDate('');
    setToDate('');
    fetchClientShipments(false);
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      
      await shipmentClientService.exportClientShipments(clientId, params);
      Alert.success('Excel file downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to export Excel file');
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleBatchPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      Alert.error('Please enter a valid payment amount');
      return;
    }
    
    if (parseFloat(paymentAmount) > summary.balance_amount) {
      Alert.error(`Payment amount cannot exceed due amount of ₹${summary.balance_amount.toFixed(2)}`);
      return;
    }
    
    const confirmed = await Alert.confirm(
      `Are you sure you want to process payment of ₹${parseFloat(paymentAmount).toFixed(2)}?`,
      'Confirm Batch Payment'
    );
    
    if (!confirmed) return;
    
    try {
      setSubmitting(true);
      
      const paymentData = {
        payment_amount: parseFloat(paymentAmount),
        payment_mode: paymentMode,
        from_date: fromDate || undefined,
        to_date: toDate || undefined
      };
      
      const response = await shipmentClientService.batchPayment(clientId, paymentData);
      
      if (response.success) {
        Alert.success(response.message);
        setPaymentAmount('');
        setPaymentMode('cash');
        fetchClientShipments(fromDate || toDate);
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to process batch payment');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const getPaymentStatusBadge = (mode) => {
    const statusColors = {
      to_pay: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      paid_balance: 'bg-blue-100 text-blue-800',
      credit: 'bg-blue-100 text-blue-800'
    };
    
    return statusColors[mode] || 'bg-gray-100 text-gray-800';
  };

  const formatPaymentMode = (mode) => {
    return mode.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <PageLayout
      title="Vendor Batch Payment"
      icon={FileText}
      actions={
        <>
          <Button icon={Download} onClick={handleExportExcel} loading={exporting} disabled={shipments.length === 0}>
            Export Excel
          </Button>
          <Button variant="outline" onClick={() => navigate('/shipment-clients')}>
            Back to Clients
          </Button>
        </>
      }
    >
      {client && (
        <p className="text-gray-600 mb-4">
          {client.full_name} - {client.company_name}
        </p>
      )}
                {/* Client Summary Card */}
                {client && (
                  <div className="bg-white p-6 rounded-sm shadow-sm mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Client Account Summary</h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-brand-light p-4 rounded-sm">
                        <p className="text-sm text-gray-600 mb-1">Total Amount</p>
                        <p className="text-2xl font-bold text-brand">
                          ₹{client.total_amount?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-sm">
                        <p className="text-sm text-gray-600 mb-1">Paid Amount</p>
                        <p className="text-2xl font-bold text-green-600">
                          ₹{client.paid_amount?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="bg-red-50 p-4 rounded-sm">
                        <p className="text-sm text-gray-600 mb-1">Due Amount</p>
                        <p className="text-2xl font-bold text-red-600">
                          ₹{client.due_amount?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-sm">
                        <p className="text-sm text-gray-600 mb-1">Total Shipments</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {summary.total_shipments}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Filters */}
                <div className="bg-white p-6 rounded-sm shadow-sm mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Filter className="w-5 h-5 text-gray-700" />
                    <h3 className="text-lg font-semibold text-gray-900">Date Filters</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        From Date
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        To Date
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                      />
                    </div>
                    <div className="flex items-end gap-2">
                      <button
                        onClick={handleApplyFilters}
                        className="flex-1 px-4 py-2 bg-buttonBg text-white rounded-sm hover:bg-blue-700 transition-all"
                      >
                        Apply Filters
                      </button>
                      <button
                        onClick={handleClearFilters}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-all"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>

                {/* Shipments Summary */}
                <div className="bg-white p-6 rounded-sm shadow-sm mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Filtered Shipments Summary</h2>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="border border-gray-200 p-4 rounded-sm">
                      <p className="text-sm text-gray-600 mb-1">Shipments</p>
                      <p className="text-xl font-bold text-gray-900">{summary.total_shipments}</p>
                    </div>
                    <div className="border border-gray-200 p-4 rounded-sm">
                      <p className="text-sm text-gray-600 mb-1">Total</p>
                      <p className="text-xl font-bold text-brand">
                        ₹{summary.total_amount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="border border-gray-200 p-4 rounded-sm">
                      <p className="text-sm text-gray-600 mb-1">Paid</p>
                      <p className="text-xl font-bold text-green-600">
                        ₹{summary.paid_amount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="border border-gray-200 p-4 rounded-sm">
                      <p className="text-sm text-gray-600 mb-1">Due</p>
                      <p className="text-xl font-bold text-red-600">
                        ₹{summary.balance_amount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Batch Payment Form */}
                {summary.balance_amount > 0 && (
                  <div className="bg-white p-6 rounded-sm shadow-sm mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <h3 className="text-lg font-semibold text-gray-900">Process Batch Payment</h3>
                    </div>
                    <form onSubmit={handleBatchPayment}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Amount (₹) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0.01"
                            max={summary.balance_amount}
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Max: ₹{summary.balance_amount.toFixed(2)}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Payment Mode *
                          </label>
                          <select
                            value={paymentMode}
                            onChange={(e) => setPaymentMode(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand"
                            required
                          >
                            <option value="cash">Cash</option>
                            <option value="upi">UPI</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cheque">Cheque</option>
                            <option value="online">Online</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <button
                            type="submit"
                            disabled={submitting || !paymentAmount}
                            className="w-full px-6 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                          >
                            {submitting ? 'Processing...' : 'Process Payment'}
                          </button>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4">
                        <p className="text-sm text-yellow-800">
                          <strong>Note:</strong> Payment will be allocated to unpaid shipments (TO_PAY, CREDIT, PAID_BALANCE) in chronological order (oldest first).
                        </p>
                      </div>
                    </form>
                  </div>
                )}

                {/* Shipments Table */}
                <div className="bg-white rounded-sm shadow-sm">
                  <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Shipment Details</h2>
                  </div>
                  
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
                      Loading shipments...
                    </div>
                  ) : shipments.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">
                      No shipments found for the selected filters
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipment ID</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sender</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receiver</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {shipments.map((shipment) => (
                            <tr key={shipment.shipment_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <button
                                  onClick={() => navigate(`/shipments/view/${shipment.shipment_id}`)}
                                  className="text-blue-600 hover:text-blue-800 font-medium"
                                >
                                  {shipment.shipment_id}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {new Date(shipment.created_at).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {shipment.sender_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                                {shipment.receiver_name}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                  {shipment.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium">
                                ₹{shipment.total_amount?.toFixed(2) || '0.00'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                                ₹{shipment.paid_amount?.toFixed(2) || '0.00'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600 font-medium">
                                ₹{shipment.balance_amount?.toFixed(2) || '0.00'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusBadge(shipment.mode_of_payment)}`}>
                                  {formatPaymentMode(shipment.mode_of_payment)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
    </PageLayout>
  );
};

export default VendorBatchShipmentBill;